import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from 'v2/configs/rq-setup'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { conversationService } from 'v2/shared/services/conversations-service'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { useTaskStore } from 'v2/state/task'
import { TaskViewName } from 'v2/types'
import {
  AdhocChannels,
  type RChunk,
  type RTask,
  type TaskActivityPayload,
  type TaskPayload,
  type TaskSearchPayload,
} from 'v2/types/api/conversations'
import { ChunkOrigin } from 'v2/types/chunk.external'
import { addChunkToQueryCache, updateChunkInQueryCache } from '../mutations/conversations'
import { updateTaskActivityInQueryCache, updateTaskInQueryCache } from '../mutations/tasks'
import { useInvitedContactsQuery } from '../queries'
import { toChunkModel } from '../utils/transformers'
import { TaskAssignmentStatus } from './tasks.config'

export const useConversationTasksQuery = (conversationNumber: string) =>
  useQuery({
    queryKey: conversationQueryKeys().conversationTasks(conversationNumber),
    queryFn: async () => {
      const response = await conversationService.getTasks({
        filters: {
          conversation_number: conversationNumber,
        },
        page: 1,
        limit: 50,
      })
      return response.data
    },
  })

export const useAllTasksQuery = (viewName: TaskViewName) => {
  const viewFilters = useTaskStore((state) => state.viewFilters[viewName])
  const { data: profile } = useProfileQuery()
  const agentUuid = profile?.organizations?.[0]?.id
  const overrideFilters: TaskSearchPayload['filters'] = {}

  if (viewName === TaskViewName.MyTasks && agentUuid) {
    overrideFilters.owner_uuids = [agentUuid]
    overrideFilters.assignment_status = [TaskAssignmentStatus.accepted]
  } else if (viewName === TaskViewName.Exceptions) {
    overrideFilters.assignment_status = [TaskAssignmentStatus.declined, TaskAssignmentStatus.failed]
  }

  return useQuery({
    queryKey: conversationQueryKeys().tasks(viewName, viewFilters),
    queryFn: async () => {
      const response = await conversationService.getTasks({
        page: (viewFilters?.pagination?.pageIndex ?? 0) + 1,
        limit: viewFilters?.pagination?.pageSize ?? 50,
        filters: {
          search_term: viewFilters?.search_query,
          ...viewFilters?.filters,
          ...overrideFilters,
        },
      })
      return response.data
    },
    enabled: viewName !== TaskViewName.MyTasks || Boolean(agentUuid),
  })
}

export const useUnassignedTasksQuery = () => {
  const { data: invitedConversations, isLoading } = useInvitedContactsQuery()
  const filteredUnassignedTasks =
    invitedConversations
      ?.map((conv) => {
        if (![AdhocChannels.CALLBACK, AdhocChannels.TASK].includes(conv.channel as AdhocChannels))
          return null

        const chunks = conv.chunks as RChunk[]
        if (!chunks?.length || !chunks[0]?.attributes?.length) return null

        const task = chunks[0].attributes[0] as RTask
        if (!task) return null

        task.conversation = conv
        return task
      })
      .filter((task): task is RTask => task !== null) || []

  return {
    data: {
      tasks: filteredUnassignedTasks,
      total_count: filteredUnassignedTasks.length,
    },
    isLoading,
  }
}

export const useCreateTaskMutation = (conversationNumber: string) =>
  useMutation({
    mutationFn: async (data: TaskPayload) => {
      const response = await conversationService.createTask(data)
      return response.data
    },
    onSuccess: (data) => {
      updateTaskInQueryCache({
        conversationId: conversationNumber,
        operation: 'create',
        data: data.attributes?.[0] as RTask,
      })
      addChunkToQueryCache(toChunkModel(data, ChunkOrigin.apiFetchChunks), conversationNumber)
    },
  })

export const useUpdateTaskMutation = (conversationNumber: string, taskUuid: string) =>
  useMutation({
    mutationFn: async ({ payload }: { payload: Partial<TaskPayload> }) => {
      if (!taskUuid) {
        return null
      }
      const response = await conversationService.updateTask(payload, taskUuid)
      return response.data
    },
    onSuccess: (data) => {
      updateChunkInQueryCache(
        conversationNumber,
        (chunk) => (chunk.meta as RTask)?.uuid === taskUuid,
        () => ({
          meta: data as RTask,
        }),
      )
      updateTaskInQueryCache({
        conversationId: conversationNumber,
        operation: 'update',
        taskUuid,
        data: data as RTask,
      })
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().taskActivities(taskUuid),
      })
    },
  })

export const useGetTaskActivities = (taskUuid: string) =>
  useQuery({
    queryKey: conversationQueryKeys().taskActivities(taskUuid),
    enabled: !!taskUuid,
    queryFn: async () => {
      const response = await conversationService.getTaskActivities(taskUuid)
      return response.data?.activities
    },
  })

export const useCreateTaskActivityMutation = (taskUuid: string) =>
  useMutation({
    mutationFn: async ({ payload }: { payload: TaskActivityPayload }) => {
      const response = await conversationService.createTaskActivity(payload, taskUuid)
      return response.data
    },
    onSuccess: (data) => {
      updateTaskActivityInQueryCache({
        taskUuid,
        operation: 'create',
        data,
      })
    },
  })

export const useUpdateTaskActivityMutation = (taskUuid: string, activityUuid: string) =>
  useMutation({
    mutationFn: async ({ payload }: { payload: TaskActivityPayload }) => {
      const response = await conversationService.updateTaskActivity(payload, taskUuid, activityUuid)
      return response.data
    },
    onSuccess: (data) => {
      updateTaskActivityInQueryCache({
        taskUuid,
        operation: 'update',
        activityId: activityUuid,
        data,
      })
    },
  })

export const useDeleteTaskActivityMutation = (taskUuid: string, activityUuid: string) =>
  useMutation({
    mutationFn: async () => {
      const response = await conversationService.deleteTaskActivity({ taskUuid, activityUuid })
      return response.data
    },
    onSuccess: () => {
      updateTaskActivityInQueryCache({
        taskUuid,
        operation: 'delete',
        activityId: activityUuid,
      })
    },
  })
